import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Basket,
  Car,
  CreditCard,
  CubeSend,
  Dots,
  Eye,
  Pencil,
  Receipt,
  SendOff,
  X
} from 'tabler-icons-react';
import dayjs from 'dayjs';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { ActionIcon, Badge, Button, Checkbox, Menu } from '@mantine/core';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import { Context as AdminContext } from '../../../providers/AdminContextProvider';
import TableView from '../../common/TableView';
import {
  ECOM_ORDER_STATUS_ENUM,
  VIEW_ACTIONS_ENUM
} from '../../../config/constants';
import { useModalState, usePaginationFilter } from '../../../helpers/hooks';
import HeaderView from '../../common/HeaderView';
import { triggerNotification } from '../../../helpers/notification';
import {
  currencyFormat,
  formatUtcDate,
  singularPluralFormat
} from '../../../helpers/format';
import OrderTransactionModal from '../orders/OrderTransactionModal';
import ExportCsvModal from '../bulkEdit/ExportCsvModal';
import { orderExportConfig } from '../orders/orderExportConfig';
import { getOrderSummaryInfo } from '../../../helpers/storeHelper';
import ConfirmModal from '../../common/ConfirmModal';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import AppMenu from '../../common/AppMenu';
import AdminOrderUpdateExternalIdModal from '../admin/AdminOrderUpdateExternalIdModal';
import { EXTERNAL_CONNECTION_CONFIGS } from '../external/config/externalConnectionConfigs';
import StoreOrderUnsubmitModal from './StoreOrderUnsubmitModal';

const VIEW_ACTIONS = [
  {
    label: 'View order',
    value: VIEW_ACTIONS_ENUM.VIEW,
    section: 1,
    icon: <Eye size={18} />
  },
  {
    label: 'View transaction',
    value: 'transaction',
    section: 1,
    icon: <Receipt size={18} />,
    isVisible: ({ isBulkOrders }) => !isBulkOrders
  },
  {
    label: 'Submit order',
    value: 'submit_order',
    section: 1,
    icon: <CubeSend size={18} />,
    isVisible: ({ isAccumulating }) => isAccumulating
  },
  {
    label: 'Unsubmit order',
    value: 'unsubmit_order',
    section: 2,
    color: '#C40000',
    icon: <SendOff size={18} />,
    isVisible: ({ isBulkOrders, isAccumulating }) =>
      isBulkOrders && !isAccumulating
  },
  {
    label: 'Complete offline',
    value: 'complete_offline',
    section: 3,
    icon: <Car size={18} />,
    isVisible: ({ isAccumulating, isAdmin }) => isAccumulating && isAdmin
  },
  {
    label: 'Cancel',
    value: 'cancel',
    section: 2,
    color: '#C40000',
    icon: <X color="#C40000" size={18} />,
    isVisible: ({ isCancelled, isBulkOrders }) => !isCancelled && !isBulkOrders
  },
  {
    label: 'Refund',
    value: VIEW_ACTIONS_ENUM.REFUND,
    section: 2,
    color: '#C40000',
    icon: <CreditCard color="#C40000" size={18} />,
    isVisible: ({ isRefunded, isBulkOrders }) => !isRefunded && !isBulkOrders
  },
  {
    label: 'Submit external',
    value: 'submit-external',
    section: 3,
    sectionTitle: 'Admin',
    icon: <Basket size={18} />,
    isVisible: ({ isAdminView, externalOrderInfo, isMissingExternalOrder }) =>
      isAdminView && externalOrderInfo.length > 0 && isMissingExternalOrder
  },
  {
    label: 'Update external ID',
    value: 'update-external-id',
    section: 3,
    sectionTitle: 'Admin',
    icon: <Pencil size={18} />,
    isVisible: ({ isAdminView, externalOrderInfo }) =>
      isAdminView && externalOrderInfo.length > 0
  },
  {
    label: 'Cancel external',
    value: 'cancel-external',
    section: 3,
    sectionTitle: 'Admin',
    color: '#C40000',
    icon: <X color="#C40000" size={18} />,
    isVisible: ({ isAdminView, externalOrderInfo }) =>
      isAdminView && externalOrderInfo.length > 0
  }
];

const TABLE_COLUMNS = [
  {
    label: 'Order',
    value: 'order',
    sortable: true
  },
  {
    label: 'Date',
    value: 'date',
    sortable: true
  },
  {
    label: 'Fulfillment status',
    value: 'status',
    sortable: true
  },
  {
    label: 'Payment status',
    value: 'payment_status',
    sortable: true,
    bulkHidden: true
  },
  {
    label: 'Store',
    value: 'store',
    sortable: true,
    isAdminOnly: true
  },
  {
    label: 'Provider(s)',
    value: 'providers',
    sortable: true,
    isAdminOnly: true
  },
  {
    label: 'Customer',
    value: 'customer',
    sortable: true
  },
  {
    label: 'Products ordered',
    value: 'products',
    sortable: true
  },
  {
    label: 'Total paid',
    value: 'total',
    sortable: true
  }
];

const StoreOrderTable = ({ isBulkOrders, isAdminView }) => {
  const fetchedTableType = useRef(null);
  const navigate = useNavigate();
  const { search } = useLocation();
  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();
  const { state: authState } = useContext(AuthContext);
  const {
    state: adminState,
    fetchPaginatedEcomOrders,
    submitEcomOrderExternalOrders,
    cancelEcomOrderExternalOrders
  } = useContext(AdminContext);
  const { state, fetchPaginatedStoreOrders } = useContext(StoreContext);
  const [selectedRows, setSelectedRows] = useState([]);
  const {
    pageIndex,
    totalCount,
    totalPages,
    totalNoFilterCount,
    data: orderData,
    filter,
    loading: dataLoading,
    isDescendingSort,
    onPageChange,
    onFilterChange,
    onSortChange,
    onRefresh,
    onReset
  } = usePaginationFilter(
    {
      search: '',
      status: '',
      sort: 'order_desc'
    },
    (newFilter) => {
      if (isAdminView) {
        fetchPaginatedEcomOrders(newFilter, null, (error) => {
          triggerNotification(error);
        });
      }
      else {
        fetchPaginatedStoreOrders(
          authState.pkEcomStore,
          newFilter,
          null,
          (error) => {
            triggerNotification(error);
          }
        );
      }
    },
    isAdminView
      ? isBulkOrders
        ? adminState.paginatedEcomOrdersBulk
        : adminState.paginatedEcomOrders
      : isBulkOrders
      ? state.paginatedEcomOrdersBulk
      : state.paginatedEcomOrders
  );
  const tableInfo = isBulkOrders
    ? {
        title: 'Group orders',
        type: 'bulk',
        urlBase: `/${isAdminView ? 'admin' : 'merchant'}/group-orders`
      }
    : {
        title: 'Orders',
        type: 'normal',
        urlBase: `/${isAdminView ? 'admin' : 'merchant'}/orders`
      };

  const loading = fetchedTableType.current !== tableInfo.type || dataLoading;
  const hasOrders = !loading && totalNoFilterCount > 0;
  const selectedOrders = orderData.filter((d) =>
    selectedRows.includes(d.pkEcomOrder)
  );

  useEffect(() => {
    if (authState.pkEcomStore) {
      onReset({
        isBulkShipment: isBulkOrders,
        search: new URLSearchParams(search).get('customer') || '',
        escrowOrders: new URLSearchParams(search).get('type') === 'escrow',
        sort: 'order_desc'
      });
      fetchedTableType.current = tableInfo.type;
    }
  }, [authState.pkEcomStore]);

  return (
    <HeaderView
      breadcrumbs={[
        {
          title: tableInfo.title,
          to: tableInfo.urlBase
        }
      ]}
      rightSection={
        hasOrders && (
          <Button
            color="dark"
            onClick={() => onOpenModal(VIEW_ACTIONS_ENUM.EXPORT, selectedRows)}
            radius="md"
            size="compact-md"
            style={{ fontSize: 14 }}
            variant="outline"
            visibleFrom="xsm"
          >
            Export
          </Button>
        )
      }
    >
      <TableView
        columns={TABLE_COLUMNS.filter(
          (f) =>
            (!f.bulkHidden || !isBulkOrders) && (!f.isAdminOnly || isAdminView)
        )}
        disabled={!loading && totalNoFilterCount === 0}
        disabledContent={{
          title: 'No orders found.',
          description:
            'Orders will appear here once customers begin to place orders.',
          icon: <Receipt color="#000" size={125} />
        }}
        emptyMessage="No orders found."
        enableInputsWhenLoading
        filters={[
          {
            key: 1,
            label: 'Search',
            placeholder: 'Filter by customer',
            value: filter.search || '',
            onChange: (v) =>
              onFilterChange({
                ...filter,
                search: v
              })
          },
          {
            key: 2,
            label: 'Status',
            type: 'select',
            value: filter.status || null,
            placeholder: 'Filter by status',
            searchable: true,
            clearable: true,
            data: [
              ...(isBulkOrders
                ? [{ value: 'accumulating', label: 'Accumulating' }]
                : []),
              { value: 'unfulfilled', label: 'Unfulfilled' },
              { value: 'fulfilled', label: 'Fulfilled' },
              { value: 'refunded', label: 'Refunded' },
              { value: 'cancelled', label: 'Cancelled' },
              ...(isAdminView
                ? [{ value: 'action-required', label: 'Action required' }]
                : [])
            ],
            onChange: (value) =>
              onFilterChange({ ...filter, status: value }, true)
          },
          ...(isAdminView
            ? [
                {
                  key: 3,
                  label: 'Provider',
                  type: 'select',
                  value: filter.fkEcomExternalConnectionType ?? null,
                  placeholder: 'Filter by provider',
                  searchable: true,
                  clearable: true,
                  data: EXTERNAL_CONNECTION_CONFIGS.map((c) => ({
                    label: c.title,
                    value: c.pkEcomExternalConnectionType.toString()
                  })),
                  onChange: (fkEcomExternalConnectionType) =>
                    onFilterChange(
                      { ...filter, fkEcomExternalConnectionType },
                      true
                    )
                }
              ]
            : [])
        ]}
        isDescendingSort={isDescendingSort}
        isLoading={loading}
        noSort
        onAction={(action, item) => {
          if (action === VIEW_ACTIONS_ENUM.VIEW) {
            navigate(`${tableInfo.urlBase}/${item.key}`);
          }
          else if (action === VIEW_ACTIONS_ENUM.REFUND) {
            navigate(`${tableInfo.urlBase}/${item.key}/refund`);
          }
          else if (action === 'cancel') {
            navigate(`${tableInfo.urlBase}/${item.key}?modal=cancel`);
          }
          else if (action === 'complete_offline') {
            navigate(`${tableInfo.urlBase}/${item.key}?modal=complete_offline`);
          }
          else if (action === 'submit_order') {
            navigate(`${tableInfo.urlBase}/${item.key}?modal=submit_order`);
          }
          else {
            onOpenModal(action, [
              orderData.find((o) => o.pkEcomOrder === item.key)
            ]);
          }
        }}
        onChangeSortBy={onSortChange}
        onPageChange={onPageChange}
        onRefresh={onRefresh}
        onSelect={setSelectedRows}
        pageIndex={pageIndex}
        rows={orderData.map((d) => {
          const {
            completelyRefunded,
            bulkInfo,
            productInfo,
            isFullyCancelled,
            totalPaid,
            minimumQuantity,
            statusInfo,
            paymentStatusInfo,
            externalOrderInfo,
            isMissingExternalOrder
          } = getOrderSummaryInfo(d, isBulkOrders);

          return {
            key: d.pkEcomOrder,
            actions: VIEW_ACTIONS.filter(
              (f) =>
                !f.isVisible ||
                f.isVisible({
                  isCancelled: isFullyCancelled,
                  isRefunded: completelyRefunded,
                  isBulkOrders,
                  isAccumulating:
                    d.fkEcomOrderStatus.toString() ===
                    ECOM_ORDER_STATUS_ENUM.ACCUMULATING,
                  isAdmin: authState.userData?.isAdmin,
                  isAdminView,
                  externalOrderInfo,
                  isMissingExternalOrder
                })
            ),
            columns: [
              {
                key: 1,
                label: `#${d.pkEcomOrder}`,
                weight: 500,
                component: Link,
                to: `${tableInfo.urlBase}/${d.pkEcomOrder}`
              },
              {
                key: 3,
                label: dayjs(formatUtcDate(d.purchasedAt)).format(
                  'MMMM D, YYYY h:mm A'
                )
              },
              {
                key: 4,
                customComponent: (
                  <Badge color={statusInfo.color} variant="light">
                    {statusInfo.label}
                  </Badge>
                )
              },
              ...(isBulkOrders
                ? []
                : [
                    {
                      key: 5,
                      customComponent: (
                        <Badge color={paymentStatusInfo.color} variant="light">
                          {paymentStatusInfo.label}
                        </Badge>
                      )
                    }
                  ]),
              ...(isAdminView
                ? [
                    {
                      key: 6,
                      label: d.ecomStore.name,
                      weight: 500,
                      subLabel: d.ecomStore.domain,
                      component: Link,
                      to: `${tableInfo.urlBase}/${d.pkEcomOrder}`
                    },
                    {
                      key: 7,
                      label:
                        externalOrderInfo.length > 0
                          ? externalOrderInfo.map((c) => c.title).join(', ')
                          : 'N/A',
                      weight: 500,
                      subLabel: isMissingExternalOrder
                        ? 'Not submitted'
                        : externalOrderInfo.length === 1
                        ? externalOrderInfo[0].orderId
                        : null,
                      subLabelColor: isMissingExternalOrder ? '#C40000' : null
                    }
                  ]
                : []),
              {
                key: 8,
                label: d.ecomCustomer.user?.name,
                weight: 500,
                subLabel: d.ecomCustomer.email,
                component: Link,
                to: `${tableInfo.urlBase}/${d.pkEcomOrder}`
              },
              {
                key: 9,
                label: isBulkOrders
                  ? `${
                      productInfo.itemCount + bulkInfo.itemCount
                    } / ${minimumQuantity} ${
                      d.ecomOrderProductBulk[0]?.ecomOrderProduct.productName ||
                      ''
                    }`
                  : productInfo.itemCount + bulkInfo.itemCount,
                sortValue: productInfo.itemCount + bulkInfo.itemCount
              },
              {
                key: 10,
                label: currencyFormat(totalPaid / 100)
              }
            ]
          };
        })}
        selectActionSection={
          isAdminView ? (
            <AppMenu
              control={
                <ActionIcon color="#ced4da" size="lg" variant="outline">
                  <Dots color="#000" />
                </ActionIcon>
              }
              styles={{ dropdown: { padding: 0 } }}
            >
              <AppStack style={{ padding: 2, gap: 1 }}>
                <Menu.Item
                  leftSection={<Basket size={18} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onOpenModal('submit-external', selectedOrders);
                  }}
                  visibleFrom="xsm"
                >
                  Submit external
                </Menu.Item>
                <Menu.Item
                  leftSection={<X color="#C40000" size={18} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onOpenModal('cancel-external', selectedOrders);
                  }}
                  style={{ color: '#c40000' }}
                  visibleFrom="xsm"
                >
                  Cancel external
                </Menu.Item>
              </AppStack>
            </AppMenu>
          ) : null
        }
        selectedRows={selectedRows}
        selectInputOnly
        sortBy={filter.sort || TABLE_COLUMNS[0].value}
        tableTitle="Orders"
        totalCount={totalCount}
        totalPages={totalPages}
      />

      <OrderTransactionModal
        isOpen={modalState.isOpen && modalState.action === 'transaction'}
        onClose={onCloseModal}
        onShowRefund={() =>
          onOpenModal(VIEW_ACTIONS_ENUM.REFUND, modalState.item[0])
        }
        pkEcomOrder={
          modalState.action === 'transaction' ? modalState.item[0] : null
        }
      />

      <ExportCsvModal
        descriptor="orders"
        exportConfig={orderExportConfig}
        isOpen={
          modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.EXPORT
        }
        itemType="order"
        itemTypePlural="orders"
        onClose={onCloseModal}
        onExport={({ items }, downloadCsvCallback, onErrorCallback) => {
          fetchPaginatedStoreOrders(
            authState.pkEcomStore,
            {
              pkEcomOrders: items.map((i) => i.pkEcomOrder),
              skipStateUpdate: true,
              pageSize: 500,
              isBulkShipment: isBulkOrders,
              sort: 'order_desc'
            },
            (result) => {
              const csvData = orderExportConfig.getCsvData(result.data);
              downloadCsvCallback(csvData);
            },
            onErrorCallback
          );
        }}
        selectedItems={selectedOrders}
      />

      <StoreOrderUnsubmitModal
        isOpen={modalState.isOpen && modalState.action === 'unsubmit_order'}
        onClose={onCloseModal}
        onSuccess={onRefresh}
        pkEcomOrder={
          modalState.item ? modalState.item[0]?.pkEcomOrder : null || null
        }
      />

      {isAdminView && (
        <>
          <ConfirmModal
            confirmActionText={`Yes, submit ${singularPluralFormat(
              modalState.item?.length,
              'order',
              'orders'
            )}`}
            isLoading={modalState.loading}
            isOpen={
              modalState.isOpen && modalState.action === 'submit-external'
            }
            message={
              modalState.item && (
                <AppStack style={{ gap: 10 }}>
                  <AppText style={{ fontSize: 16 }}>
                    Are you sure you want to submit{' '}
                    <span style={{ fontWeight: 500 }}>
                      {modalState.item.length === 1
                        ? `order #${modalState.item[0].pkEcomOrder}`
                        : `${modalState.item.length} orders`}
                    </span>{' '}
                    to their external providers?
                  </AppText>

                  <AppText
                    style={{ fontSize: 14, color: '#666', fontStyle: 'italic' }}
                  >
                    Orders already sumbitted or without an external provider
                    will be skipped
                  </AppText>
                </AppStack>
              )
            }
            onCancel={onCloseModal}
            onConfirm={() => {
              onChangeModalLoading(true);
              submitEcomOrderExternalOrders(
                { pkEcomOrders: modalState.item.map((i) => i.pkEcomOrder) },
                () => {
                  onRefresh();
                  triggerNotification('Order submitted.', 'success');
                  onCloseModal();
                },
                (error) => {
                  triggerNotification(error);
                  onChangeModalLoading(false);
                }
              );
            }}
            title={`Submit external orders${
              modalState.item?.length === 1
                ? ` for ${modalState.item[0].pkEcomOrder}`
                : ''
            }?`}
          />

          <ConfirmModal
            confirmActionText={`Yes, cancel ${singularPluralFormat(
              modalState.item?.length,
              'order',
              'orders'
            )}`}
            isLoading={modalState.loading}
            isOpen={
              modalState.isOpen && modalState.action === 'cancel-external'
            }
            message={
              modalState.item && (
                <AppStack style={{ gap: 10 }}>
                  <AppText style={{ fontSize: 16 }}>
                    Are you sure you want to cancel{' '}
                    <span style={{ fontWeight: 500 }}>
                      {modalState.item.length === 1
                        ? `order #${modalState.item[0].pkEcomOrder}`
                        : `${modalState.item.length} orders`}
                    </span>{' '}
                    with their external providers?
                  </AppText>

                  <AppText
                    style={{ fontSize: 14, color: '#666', fontStyle: 'italic' }}
                  >
                    Orders already cancelled or without an external provider
                    will be skipped
                  </AppText>

                  <Checkbox
                    disabled={modalState.loading}
                    label="I confirm this action"
                    required
                  />
                </AppStack>
              )
            }
            onCancel={onCloseModal}
            onConfirm={() => {
              onChangeModalLoading(true);
              cancelEcomOrderExternalOrders(
                { pkEcomOrders: modalState.item.map((i) => i.pkEcomOrder) },
                () => {
                  onRefresh();
                  triggerNotification('Order cancelled.', 'success');
                  onCloseModal();
                },
                (error) => {
                  triggerNotification(error);
                  onChangeModalLoading(false);
                }
              );
            }}
            title={`Cancel external orders${
              modalState.item?.length === 1
                ? ` for ${modalState.item[0].pkEcomOrder}`
                : ''
            }?`}
          />

          <AdminOrderUpdateExternalIdModal
            ecomOrder={
              modalState.isOpen &&
              modalState.action === 'update-external-id' &&
              modalState.item?.length === 1
                ? modalState.item[0]
                : null
            }
            isOpen={
              modalState.isOpen && modalState.action === 'update-external-id'
            }
            onClose={onCloseModal}
            onSuccess={() => {
              onRefresh();
              onCloseModal();
            }}
          />
        </>
      )}
    </HeaderView>
  );
};

StoreOrderTable.propTypes = {
  isAdminView: PropTypes.bool,
  isBulkOrders: PropTypes.bool
};

export default StoreOrderTable;
